/**
 * Generic undo/redo history stack.
 * Stores snapshots of state and allows traversal.
 */

export interface UndoStack<T> {
  push: (state: T) => void;
  undo: () => T | null;
  redo: () => T | null;
  canUndo: boolean;
  canRedo: boolean;
  clear: () => void;
}

export function createUndoStack<T>(maxDepth = 50): UndoStack<T> {
  const past: T[] = [];
  const future: T[] = [];

  return {
    push(state: T) {
      past.push(state);
      if (past.length > maxDepth) past.shift();
      future.length = 0; // Clear redo stack on new action
    },

    undo(): T | null {
      if (past.length === 0) return null;
      const state = past.pop()!;
      future.push(state);
      return past.length > 0 ? past[past.length - 1] : null;
    },

    redo(): T | null {
      if (future.length === 0) return null;
      const state = future.pop()!;
      past.push(state);
      return state;
    },

    get canUndo() {
      return past.length > 1; // Need at least 2 items (current + previous)
    },

    get canRedo() {
      return future.length > 0;
    },

    clear() {
      past.length = 0;
      future.length = 0;
    },
  };
}
